import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  Alert, AutoComplete, Button, Form, Input, InputNumber, Modal, Radio, Select, Space, Table, Tag, Tooltip, message,
} from 'antd';

import { responsiveSpacing } from '../../themes/responsive';
import { useAuth } from '../../context/AuthContext';
import { fetchCostRules, createCostRule, updateCostRule } from '../../data/costRulesApi';

const METHOD_KEYS = {
  per_unit: 'costRuleMethodPerUnit',
  pct_price: 'costRuleMethodPctPrice',
  vat_included: 'costRuleMethodVatIncluded',
};
const isPercent = (method) => method !== 'per_unit';
// Percentages travel as fractions (0.10); the form shows them as 10.
const toDisplayValue = (rule) => (isPercent(rule.method) ? Math.round(rule.value * 10000) / 100 : rule.value);

// Each modal owns its form and is mounted fresh on every opening, so it never shows a previous input.
const RuleModal = ({ title, initialValues, lockTag, saving, onSubmit, onCancel, options }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const scopeType = Form.useWatch('scopeType', form) ?? initialValues.scopeType;
  const method = Form.useWatch('method', form) ?? initialValues.method;
  const previousMethod = React.useRef(initialValues.method);

  return (
    <Modal
      open
      title={title}
      okText={t('save')}
      cancelText={t('cancel')}
      confirmLoading={saving}
      onOk={() => form.validateFields().then(onSubmit).catch(() => {})}
      onCancel={onCancel}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={(changed) => {
          // Switching between € and % converts the amount (0.05 € <-> 5 %) so it keeps reading right.
          if (!('method' in changed)) return;
          const before = previousMethod.current;
          previousMethod.current = changed.method;
          const value = form.getFieldValue('value');
          if (value == null || isPercent(before) === isPercent(changed.method)) return;
          const converted = isPercent(changed.method) ? value * 100 : value / 100;
          form.setFieldsValue({ value: Math.round(converted * 10000) / 10000 });
        }}
      >
        <Form.Item name="component" label={t('costRuleComponent')} rules={[{ required: true, whitespace: true, message: t('costRuleRequired') }]}>
          <AutoComplete options={options.components} filterOption />
        </Form.Item>
        <Form.Item name="method" label={t('costRuleMethod')} rules={[{ required: true, message: t('costRuleRequired') }]}>
          <Select options={Object.entries(METHOD_KEYS).map(([value, key]) => ({ value, label: t(key) }))} />
        </Form.Item>
        <Form.Item
          name="value"
          label={`${t('costRuleValue')} (${isPercent(method) ? '%' : t('costRuleMethodPerUnit')})`}
          extra={isPercent(method) ? t('costRuleValuePctHelp') : undefined}
          rules={[{ required: true, message: t('costRuleRequired') }]}
        >
          <InputNumber min={0} max={isPercent(method) ? 100 : undefined} step={0.01} addonAfter={isPercent(method) ? '%' : '€'} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="scopeType" label={t('costRuleAppliesTo')}>
          <Radio.Group>
            <Radio value="all">{t('costRuleScopeAll')}</Radio>
            <Radio value="group">{t('costRuleScopeTypeGroup')}</Radio>
            {/* Tariffs name no products: products get their tariffs in Productos. Only a rule that
                already lists products keeps that choice, so editing it can't silently widen it. */}
            {initialValues.scopeType === 'products' && <Radio value="products">{t('costRuleScopeTypeProducts')}</Radio>}
          </Radio.Group>
        </Form.Item>
        {scopeType === 'group' && (
          <Form.Item name="taxGroup" label={t('costRuleTaxGroup')} rules={[{ required: true, whitespace: true, message: t('costRuleRequired') }]}>
            {/* A tariff in force keeps its tag: its past sales were charged through the products carrying it. */}
            <AutoComplete options={options.groups} filterOption disabled={lockTag} />
          </Form.Item>
        )}
        {scopeType === 'products' && (
          <Form.Item name="skus" label={t('costRuleProducts')} rules={[{ required: true, type: 'array', min: 1, message: t('costRuleRequired') }]}>
            <Select mode="multiple" options={options.products} optionFilterProp="label" showSearch />
          </Form.Item>
        )}
        <Form.Item name="provinces" label={t('costRuleProvinces')}>
          <Select mode="multiple" allowClear placeholder={t('costRuleAllProvinces')} options={options.provinces} />
        </Form.Item>
        <Form.Item name="validFrom" label={t('costRuleValidFrom')} rules={[{ required: true, message: t('costRuleRequired') }]}>
          <Input type="date" min={options.earliestStart} />
        </Form.Item>
        <Form.Item name="validTo" label={t('costRuleValidTo')} extra={t('costRuleValidToHelp')}>
          <Input type="date" min={options.earliestStart} />
        </Form.Item>
        <Form.Item name="note" label={t('costRuleNote')}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const SettingsCostRulesTab = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const clientId = currentUser?.customer_id;

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  // null = closed; otherwise { rule, initialValues } for the rule modal
  const [ruleModal, setRuleModal] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!clientId) return;
    try {
      setError(null);
      setData(await fetchCostRules(clientId));
    } catch (err) {
      console.error('Error loading cost rules:', err);
      setError(t('costRulesLoadError'));
    } finally {
      setLoading(false);
    }
  }, [clientId, t]);

  React.useEffect(() => { load(); }, [load]);

  const productName = React.useMemo(() => {
    const map = new Map((data?.products || []).map((p) => [p.sku, p.name]));
    return (sku) => map.get(sku) || sku;
  }, [data]);

  const options = React.useMemo(() => {
    const groups = new Set((data?.rules || []).map((r) => r.taxGroup).filter(Boolean));
    return {
      products: (data?.products || []).map((p) => ({ value: p.sku, label: `${p.name} (${p.sku})` })),
      groups: [...groups].sort().map((value) => ({ value })),
      components: [...new Set((data?.rules || []).map((r) => r.component))].sort().map((value) => ({ value })),
      provinces: (data?.provinces || []).map((p) => ({ value: p, label: p })),
      earliestStart: data?.earliestStart,
    };
  }, [data]);

  const scopeText = React.useCallback((rule) => {
    const parts = [];
    if (rule.taxGroup) parts.push(t('costRuleScopeGroup', { group: rule.taxGroup }));
    if (rule.skus) parts.push(t('costRuleScopeProducts', { products: rule.skus.map(productName).join(', ') }));
    if (rule.productCategory) parts.push(t('costRuleScopeCategory', { category: rule.productCategory }));
    if (rule.subcategory) parts.push(t('costRuleScopeCategory', { category: rule.subcategory }));
    if (parts.length === 0) parts.push(t('costRuleScopeAll'));
    parts.push(rule.provinces ? rule.provinces.join(', ') : t('costRuleAllProvinces'));
    if (rule.country) parts.push(rule.country);
    return parts.join(' · ');
  }, [t, productName]);

  const openRuleModal = (rule) => {
    const startsLater = rule && rule.validFrom > data.today;
    setRuleModal({
      rule,
      initialValues: rule ? {
        component: rule.component,
        method: rule.method,
        value: toDisplayValue(rule),
        scopeType: rule.taxGroup ? 'group' : rule.skus ? 'products' : 'all',
        taxGroup: rule.taxGroup || undefined,
        skus: rule.skus || [],
        provinces: rule.provinces || [],
        validFrom: startsLater ? rule.validFrom : data.earliestStart,
        validTo: rule.validTo || '',
        note: rule.note || '',
      } : {
        component: '', method: 'per_unit', scopeType: 'all', skus: [], provinces: [],
        validFrom: data.earliestStart, validTo: '', note: '',
      },
    });
  };

  const submitRule = async (values) => {
    const payload = {
      component: values.component.trim(),
      method: values.method,
      value: isPercent(values.method) ? values.value / 100 : values.value,
      taxGroup: values.scopeType === 'group' ? values.taxGroup : null,
      skus: values.scopeType === 'products' ? values.skus : null,
      provinces: values.provinces?.length ? values.provinces : null,
      validFrom: values.validFrom,
      validTo: values.validTo || null,
      note: values.note || null,
    };
    setSaving(true);
    try {
      if (ruleModal.rule?.id) await updateCostRule(clientId, ruleModal.rule.id, payload);
      else await createCostRule(clientId, payload);
      message.success(t('costRuleSaved'));
      setRuleModal(null);
      await load();
    } catch (err) {
      const body = err?.response?.data;
      message.error(body?.code === 'overlap' ? t('costRuleOverlapError') : body?.message || t('costRuleSaveError'));
    } finally {
      setSaving(false);
    }
  };

  // Sorted in the browser; numeric-aware so "IBEE 2" comes before "IBEE 31".
  const byText = (value) => (a, b) => value(a).localeCompare(value(b), undefined, { numeric: true, sensitivity: 'base' });
  const ruleColumns = [
    {
      title: t('costRuleComponent'), dataIndex: 'component', key: 'component',
      sorter: byText((r) => r.component),
      render: (component, r) => (r.isLaw ? (
        <Space size={4}>
          {component}
          <Tooltip title={t('costRuleLawTooltip')}><Tag color="blue">{t('costRuleSourceLaw')}</Tag></Tooltip>
        </Space>
      ) : component),
    },
    { title: t('costRuleAppliesTo'), key: 'scope', sorter: byText(scopeText), render: (_, r) => scopeText(r) },
    {
      title: t('costRuleMethod'), dataIndex: 'method', key: 'method',
      sorter: byText((r) => t(METHOD_KEYS[r.method])), render: (m) => t(METHOD_KEYS[m]),
    },
    {
      title: t('costRuleValue'), key: 'value', align: 'right',
      // Percentages and € per unit don't compare: each kind is sorted by amount within its own block.
      sorter: (a, b) => (isPercent(a.method) - isPercent(b.method)) || (a.value - b.value),
      // Per-unit taxes can have 3-4 decimals (IBEE 0.0495 €): show them, with at least 2.
      render: (_, r) => (isPercent(r.method) ? `${toDisplayValue(r)} %` : `${r.value.toFixed(4).replace(/0{1,2}$/, '')} €`),
    },
    { title: t('costRuleValidFrom'), dataIndex: 'validFrom', key: 'validFrom', sorter: byText((r) => r.validFrom) },
    {
      title: t('costRuleValidTo'), dataIndex: 'validTo', key: 'validTo',
      // Open-ended rules (still in force) sort after every end date.
      sorter: byText((r) => r.validTo || '9999-12-31'), render: (v) => v || t('costRuleOpenEnded'),
    },
    {
      title: t('actions'), key: 'actions',
      render: (_, r) => (r.editable ? (
        <Button size="small" onClick={() => openRuleModal(r)}>{t('costRuleEdit')}</Button>
      ) : null),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ ...responsiveSpacing.pagePadding }}>
      <Typography variant="h4" component="h1" gutterBottom>{t('costRules')}</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>{t('costRulesDescription')}</Typography>

      {error ? (
        <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
      ) : (
        <>
          <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('costRulesProspectiveNote', { date: data.earliestStart })} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">{t('costRulesTagsHint')}</Typography>
            <Button type="primary" onClick={() => openRuleModal(null)}>{t('costRuleNew')}</Button>
          </Box>
          <Table
            columns={ruleColumns}
            // Each client's canon is edited on its own row in Clientes.
            dataSource={data.rules.filter((r) => !r.storeGroups)}
            rowKey="id"
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: t('costRulesEmpty') }}
          />
        </>
      )}

      {ruleModal && (
        <RuleModal
          title={ruleModal.rule?.id ? t('costRuleEditTitle') : t('costRuleNew')}
          initialValues={ruleModal.initialValues}
          lockTag={Boolean(ruleModal.rule?.taxGroup && ruleModal.rule.validFrom <= data.today)}
          saving={saving}
          onSubmit={submitRule}
          onCancel={() => setRuleModal(null)}
          options={options}
        />
      )}
    </Box>
  );
};
